import { Font, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { AxiosError } from "axios";
import Button from "components/button";
import { ListItem } from "components/ListItem/ListItem";
import Loading from "components/loading";
import ResultBox from "components/result-box";
import global from "global";
import React from "react";
import { useParams } from "react-router-dom";
import request from "request";
import { useDispatch, useSelector } from "store/hooks";
import prettierNumber from "utils/prettier-number";
import styles from "./ocr.module.scss";
import { toast } from "react-toastify";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: "bold",
    },
  ],
});

export const pdfStyles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    padding: "3px 10px",
    fontWeight: "medium",
  },
  section: {
    marginBottom: "5px",
    marginTop: "5px",
  },
  heading: {
    fontSize: "20px",
    textAlign: "center",
    fontWeight: "bold",
  },
  heading2: {
    fontSize: "18px",
    textAlign: "center",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  text: {
    fontSize: "12px",
    marginBottom: "10px",
  },
  text2: {
    fontSize: "12px",
    marginTop: "10px",
  },
  table: {
    backgroundColor: "#fff",
  },
  tr: {
    display: "flex",
    flexDirection: "row",
  },
  td: {
    border: "1px solid #000",
    width: "100%",
    padding: "5px 10px",
    fontSize: "12px",
  },
  th: {
    border: "1px solid #000",
    width: "100%",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: "bold",
  },
  result: {
    fontSize: "16px",
    fontWeight: "bold",
  },
  bottom: {
    margin: "10px 0 0 0",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  bottom2: {
    marginTop: "40px",
  },
});

const OCR = () => {
  const [ocrPending, setOcrPending] = React.useState(false);
  const [filePKB, setFilePKB] = React.useState<File | null>();

  const dispatch = useDispatch();
  const contextApplication = useSelector((state) => state.application);

  const { id } = useParams();

  const handleRunOCR = async () => {
    setOcrPending(true);
    const fd = new FormData();
    if (filePKB) fd.append("pkb", filePKB);
    if (filePKB) fd.append("recognize", "pkb");
    if (filePKB) fd.append("recognize", "both");
    request({
      url: `/applications/${id}/ocr/pkb`,
      method: "POST",
      data: fd,
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((res) => res.data)
      .catch((e: AxiosError) => {
        if (e.response?.status === 409) {
          toast.error("Невалидный файл выписки!");
        }
        if (e.response?.status === 415) {
          toast.error("Пустой файл!");
        }
      })
      .finally(async () => {
        if (filePKB && contextApplication.data?.scoring_log)
          await request.post(`applications/${contextApplication.data.id}/cdn`);
        dispatch.application.FETCH_APPLICATION_BY_ID(id);
      });
  };

  const handleFileChangePKB = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFilePKB(event.target.files[0]);
    }
  };

  if (!contextApplication.data) return <></>;

  const {
    date_made,
    created_at: pkbCreatedAt,
    ...pkb
  } = contextApplication.data.pkb_ocr_log ?? {};

  return (
    <div className={styles.ocr}>
      {contextApplication.data?.ocr_log && (
        <div className={styles.fields}>
          {contextApplication.data?.ocr_log && (
            <div className={styles.creditDataBox}>
              <div className={styles.description}>
                <div>
                  OCR проведён экспертом:{" "}
                  {contextApplication.data.created_by.email}
                </div>
                <div>
                  Дата проведения OCR:{" "}
                  {new Date(
                    contextApplication.data.created_at
                  ).toLocaleString()}
                </div>
              </div>
            </div>
          )}
          {contextApplication.data?.ocr_log && (
            <div className={styles.creditDataBox}>
              <div className={styles.title}>ПКБ</div>
              <div className={styles.list}>
                <ListItem
                  title="№ заявления по микрокредиту"
                  value={contextApplication.data.user_id}
                />
                <ListItem
                  title="Дата получения сведений кредитным бюро"
                  value={new Date(
                    contextApplication.data.ocr_log.date_made
                  ).toLocaleDateString()}
                />
                <ListItem
                  title="Субъект заблокировал получение кредитного отчета"
                  value={
                    contextApplication.data.ocr_log.no_access
                      ? "Да (СТОП)"
                      : "Нет"
                  }
                />
                <ListItem
                  title="Вовлечения в игорный бизнес"
                  value={
                    contextApplication.data.ocr_log.player ? "Да (СТОП)" : "Нет"
                  }
                />
                <ListItem
                  title="Клиент признан банкротом"
                  value={
                    contextApplication.data.ocr_log.bankrupt
                      ? "Да (СТОП)"
                      : "Нет"
                  }
                />
                <ListItem
                  title="Наличие активной просрочки свыше 60 дней"
                  value={
                    contextApplication.data.ocr_log.overdue_60_days
                      ? "Да (СТОП)"
                      : "Нет"
                  }
                />
                <ListItem
                  title="Наличие активной просрочки свыше 1 дней по активным кредитам"
                  value={
                    contextApplication.data.ocr_log.overdue_1_day === null
                      ? "-"
                      : contextApplication.data.ocr_log.overdue_1_day
                      ? "Да, только для потребительских кредитов"
                      : "Нет"
                  }
                />
                <ListItem
                  title="Наличие просрочки свыше 90 дней в календаре платежей за последние 12 месяцев."
                  value={
                    contextApplication.data.ocr_log.overdue_90_days_calendar === null
                      ? "-"
                      : contextApplication.data.ocr_log.overdue_90_days_calendar
                      ? "Да, только для потребительских кредитов"
                      : "Нет"
                  }
                />
                <ListItem
                  title="Списан/прощён за последние 3 года"
                  value={
                    contextApplication.data.ocr_log.written_off === null
                      ? "-"
                      : contextApplication.data.ocr_log.written_off
                      ? "Да"
                      : "Нет"
                  }
                />
                <ListItem
                  title="Реструктуризация, пролонгация, урегулирование в течении 12 месяцев по активным кредитам"
                  value={
                    contextApplication.data.ocr_log.restructured === null
                      ? "-"
                      : contextApplication.data.ocr_log.restructured
                      ? "Да"
                      : "Нет"
                  }
                />
                <ListItem
                  title="Количество запросов в ПКБ за последние 30 дней"
                  value={contextApplication.data.ocr_log.requests_30_days}
                />
                <ListItem
                  title="Количество активных кредитов"
                  value={contextApplication.data.ocr_log?.active_loans}
                />
                <ListItem
                  title="Непогашенная/оставшаяся сумма"
                  value={prettierNumber(
                    contextApplication.data.ocr_log.outstanding_amount
                  )}
                />
                <ListItem
                  title="Просроченная сумма"
                  value={prettierNumber(
                    contextApplication.data.ocr_log.overdue_amount
                  )}
                />
                <ListItem
                  title="Общая сумма долга"
                  value={prettierNumber(
                    contextApplication.data.ocr_log.total_amount
                  )}
                />
                <ListItem
                  title="Платеж по кредитам для расчета КПД"
                  value={prettierNumber(contextApplication.data.ocr_log.kpd)}
                />
                <ListItem
                  title="Платеж по кредитам для расчета КДН"
                  value={prettierNumber(contextApplication.data.ocr_log.kdn)}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {!contextApplication.data?.ocr_log && !ocrPending && (
        <div className={styles.inputBox}>
          <label className={styles.fileInput}>
            <div className={styles.fileInputTitle}>ПКБ:</div>
            {filePKB?.name ?? (
              <div className={styles.fileInputDescription}>
                Загрузить <span className={styles.bold}>.pdf</span> файл
              </div>
            )}
            <input
              accept="application/pdf"
              type="file"
              onChange={handleFileChangePKB}
            />
          </label>
          <div className={styles.footerBox}>
            {filePKB?.name ? (
              <div className={styles.buttonGroup}>
                <Button onClick={handleRunOCR}>Запустить OCR</Button>
              </div>
            ) : (
              <div className={styles.disabled}>
                <Button onClick={handleRunOCR}>Запустить OCR</Button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.result}>
        {ocrPending && (
          <div className={styles.resultBox}>
            <Loading size={50} />
          </div>
        )}
        <ResultBox />
      </div>
    </div>
  );
};

export const OCRPdf = ({
  contextApplication,
}: {
  contextApplication: global.contextApplication;
}) => {
  if (!contextApplication.data) return <></>;

  const { first_name, last_name, email } =
    contextApplication.data.created_by ?? {};
  const {
    date_made,
    created_at: pkbCreatedAt,
    ...pkb
  } = contextApplication.data.ocr_log ?? {};

  const pkbApproved = contextApplication.data?.ocr_log?.is_approved;

  return (
    <>
      {contextApplication.data.ocr_log && (
        <Page style={pdfStyles.page}>
          <View style={pdfStyles.heading}>
            <Text>Результат OCR</Text>
          </View>
          <View style={pdfStyles.text}>
            <Text>
              OCR проведён экспертом: {[first_name, last_name, email].join(" ")}
            </Text>
            <Text>
              Дата проведения OCR:{" "}
              {new Date(contextApplication.data.created_at).toLocaleString()}
            </Text>
          </View>
          <View style={pdfStyles.heading2}>
            <Text>ПКБ</Text>
          </View>
          <View style={pdfStyles.section}>
            <View style={pdfStyles.table}>
              {/* 1. Ф.И.О клиента */}
              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"№ заявления по микрокредиту"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>{contextApplication.data?.user_id}</Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Дата получения сведений кредитным бюро"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {new Date(
                      contextApplication.data.ocr_log.date_made
                    ).toLocaleDateString()}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>
                    {"Субъект заблокировал получение кредитного отчета"}
                  </Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.no_access
                      ? "Да (СТОП)"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Вовлечения в игорный бизнес"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.player
                      ? "Да (СТОП)"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Клиент признан банкротом"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.bankrupt
                      ? "Да (СТОП)"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Наличие активной просрочки свыше 60 дней"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.overdue_60_days
                      ? "Да (СТОП)"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>
                    {"Наличие активной просрочки свыше 1 дней по активным кредитам"}
                  </Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.overdue_1_day === null
                      ? "-"
                      : contextApplication.data.ocr_log?.overdue_1_day
                      ? "Да, только для потребительских кредитов"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>
                    {"Наличие просрочки свыше 90 дней в календаре платежей за последние 12 месяцев."}
                  </Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.overdue_90_days_calendar === null
                      ? "-"
                      : contextApplication.data.ocr_log?.overdue_90_days_calendar
                      ? "Да, только для потребительских кредитов"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Списан/прощён за последние 3 года"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.written_off === null
                      ? "-"
                      : contextApplication.data.ocr_log?.written_off
                      ? "Да"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>
                    {"Реструктуризация, пролонгация, урегулирование в течении 12 месяцев по активным кредитам"}
                  </Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.restructured === null
                      ? "-"
                      : contextApplication.data.ocr_log?.restructured
                      ? "Да"
                      : "Нет"}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>
                    {"Количество запросов в ПКБ за последние 30 дней"}
                  </Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {contextApplication.data.ocr_log?.requests_30_days}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Количество активных кредитов"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>{contextApplication.data.ocr_log?.active_loans}</Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Непогашенная/оставшаяся сумма"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {prettierNumber(
                      contextApplication.data.ocr_log?.outstanding_amount
                    )}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Просроченная сумма"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {prettierNumber(
                      contextApplication.data.ocr_log?.overdue_amount
                    )}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Общая сумма долга"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {prettierNumber(
                      contextApplication?.data?.ocr_log?.total_amount
                    )}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Платеж по кредитам для расчета КПД"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {prettierNumber(contextApplication.data.ocr_log?.kpd)}
                  </Text>
                </View>
              </View>

              <View style={pdfStyles.tr}>
                <View style={pdfStyles.td}>
                  <Text>{"Платеж по кредитам для расчета КДН"}</Text>
                </View>
                <View style={pdfStyles.td}>
                  <Text>
                    {prettierNumber(contextApplication.data.ocr_log?.kdn)}
                  </Text>
                </View>
              </View>
            </View>
            {pkbApproved !== null && (
              <View style={pdfStyles.bottom}>
                <Text style={pdfStyles.result}>
                  Результат ПКБ: {pkbApproved ? "Пройден" : "Не пройден"}
                </Text>
              </View>
            )}
          </View>
        </Page>
      )}
    </>
  );
};

export default OCR;
